import React from 'react';
import {Trans, useTranslation} from 'react-i18next';
import {useMatch} from 'react-router';

import {InfoDropdown} from '@edna/components';
import {Icon, Stack} from '@edna/components/primitives';

import {Text} from 'src/components/primitives';
import {EChannelType, channelIconHash} from 'src/constants';
import {useGetTenantDataQuery} from 'src/containers/User';
import {useAuthorizedMetrics} from 'src/hooks';
import {ExternalLinkIcon, LinkIcon, OkContourIcon} from 'src/icons';
import PATHS from 'src/paths';
import {EMetricsId} from 'src/utils';

import {showLinkedSubjectsModal} from '../TariffModals';
import {useLicensePrices, usePricingLink} from '../TariffPricing';
import {TAvailableTariff, TLicenseInfo} from '../definitions';
import * as S from './style';

export const Point = React.memo<{
  children: React.ReactNode | string | React.ReactElement;
  isBold?: boolean;
}>(({children, isBold}) => {
  return (
    <S.Point isBold={isBold}>
      <Icon as={OkContourIcon} size="20px" />
      {children}
    </S.Point>
  );
});

Point.displayName = 'Point';

export const WhatsAppExtraOptions = React.memo<{
  mauCount?: TLicenseInfo['mauCount'];
  amauPrice?: TLicenseInfo['amauPrice'];
  currency?: TLicenseInfo['currency'];
}>(({mauCount, amauPrice, currency}) => {
  const {t} = useTranslation();

  return (
    <>
      {!!mauCount && (
        <Point isBold>
          <Trans i18nKey="Tariffs:whatsappPricing.mauCount" values={{mauCount}}>
            <InfoDropdown content={t('Tariffs:whatsappPricing.mauHint')} />
          </Trans>
        </Point>
      )}

      {!!amauPrice && !!currency && (
        <Point isBold>{t('Tariffs:whatsappPricing.amauPrice', {amauPrice, currency})}</Point>
      )}
    </>
  );
});

WhatsAppExtraOptions.displayName = 'WhatsAppExtraOptions';

const PricingLink = React.memo<{
  children: React.ReactNode;
  hasIcon?: boolean;
  link: string;
}>(({children, hasIcon, link}) => {
  if (!hasIcon) {
    return (
      <S.PricingLink href={link} onClick={(event) => event.stopPropagation()}>
        {children}
      </S.PricingLink>
    );
  }

  return (
    <Stack direction="ROW" align="center" size="1">
      <S.PricingLink href={link} onClick={(event) => event.stopPropagation()}>
        {children}
      </S.PricingLink>
      <Icon as={ExternalLinkIcon} size="16px" />
    </Stack>
  );
});

WhatsAppExtraOptions.displayName = 'WhatsAppExtraOptions';

export const SellingTariffContent = React.memo<{tariff: TAvailableTariff}>(({tariff}) => {
  const {t} = useTranslation();

  const link = usePricingLink({channelType: tariff.channelType, usePublicPrices: true});
  const licensePrices = useLicensePrices(tariff, true);
  const options = t(`Tariffs:tariffOptions.${tariff.channelType}`, {returnObjects: true});
  const points: string[] = Array.isArray(options) ? options : [];

  return (
    <>
      {!!licensePrices && (
        <Text weight={700}>
          {t(`Tariffs:priceByPeriod`, {
            price: licensePrices.licensePrice,
            currency: licensePrices.currency,
            period: t(`Tariffs:licensePeriod.${licensePrices.period}`).toLowerCase(),
          })}
        </Text>
      )}
      {tariff.channelType === EChannelType.SMS && (
        <PricingLink hasIcon link={link}>
          {t('Tariffs:smsPricing.messagePriceLink')}
        </PricingLink>
      )}
      {tariff.channelType === EChannelType.VIBER && (
        <>
          {licensePrices && (
            <Text weight={700}>
              {t('Tariffs:viberPricing.packagePriceShort')}
              <S.PriceWithHint>
                {t(`Tariffs:priceByPeriod`, {
                  price: licensePrices.packagePrice,
                  currency: licensePrices.currency,
                  period: t(`Tariffs:licensePeriod.${licensePrices.period}`).toLowerCase(),
                })}
                <S.Hint content={t(`Tariffs:viberPricing.packagePriceShortHint`)} />
              </S.PriceWithHint>
            </Text>
          )}
          <PricingLink hasIcon link={link}>
            {t('Tariffs:viberPricing.trafficPricing')}
          </PricingLink>
        </>
      )}
      <Text size="S" secondary>
        {t('Tariffs:tariffCard.options')}
      </Text>
      {tariff.channelType === EChannelType.WHATSAPP && (
        <WhatsAppExtraOptions
          mauCount={licensePrices?.mauCount}
          amauPrice={licensePrices?.amauPrice}
          currency={licensePrices?.currency}
        />
      )}
      {points.map((point, index) => (
        <Point key={index}>{point}</Point>
      ))}
      {tariff.channelType === EChannelType.WHATSAPP && (
        <PricingLink link={link}>{t('Tariffs:whatsappPricing.trafficPricing')}</PricingLink>
      )}
    </>
  );
});

SellingTariffContent.displayName = 'SellingTariffContent';

const metricsHash: Partial<Record<EChannelType, EMetricsId>> = {
  [EChannelType.MAX]: EMetricsId.ONBOARDING_CONNECT_TARIFF_MAX,
  [EChannelType.MAX_BOT]: EMetricsId.ONBOARDING_CONNECT_TARIFF_MAX_BOT,
  [EChannelType.WHATSAPP]: EMetricsId.ONBOARDING_CONNECT_TARIFF_WHATSAPP,
};

export const SellingTariffCard = React.memo<{
  gap?: number;
  tariff: TAvailableTariff;
  onConnect?: TEmptyFunction;
  linkedSubjects?: number[];
  count: number;
  readOnly?: boolean;
}>(({tariff, onConnect, linkedSubjects = [], count, gap, readOnly}) => {
  const {t} = useTranslation();
  const {sendMetrics} = useAuthorizedMetrics();
  const isHomePage = !!useMatch({path: PATHS.HOME, end: false});
  const {data: tenant} = useGetTenantDataQuery();

  const handleConnect = () => {
    onConnect?.();

    const metricsId = metricsHash[tariff.channelType];

    if (!metricsId || !tenant?.selfRegistered || !isHomePage) {
      return;
    }

    sendMetrics(metricsId);
  };

  return (
    <S.TariffWrapper count={count} gap={gap}>
      <S.TariffTitle>
        <Icon as={channelIconHash[tariff.channelType]} size="32px" />
        {!!linkedSubjects.length && (
          <Stack size="1">
            {tariff.name}
            <S.InlineLinkButton onClick={() => showLinkedSubjectsModal(linkedSubjects)}>
              <LinkIcon />
              {t('Tariffs:tariffCard.linkedSubjects')}
            </S.InlineLinkButton>
          </Stack>
        )}
        {!linkedSubjects.length && tariff.name}
      </S.TariffTitle>
      <SellingTariffContent tariff={tariff} />
      <div style={{flex: 1}} />
      {!readOnly && (
        <S.ConnectButton appearance="primary" onClick={handleConnect}>
          {t('Tariffs:tariffCard.connect')}
        </S.ConnectButton>
      )}
    </S.TariffWrapper>
  );
});

SellingTariffCard.displayName = 'SellingTariffCard';
